<template>
	<div class="page">
		<div class="page__receipt receipt">
			<div v-if="testMode && ticket" class="testMode">Demo mode</div>
			<div v-if="ticket" class="receipt__wrapper">
				<div v-if="ticket.daycode && !hasExpired" class="receipt__logo-container">
					<div class="receipt__logo-bg"></div>
					<div
						class="receipt__logo-content"
						:style="{ 'background-color': '#' + (onLine && ticket.daycode.colorCode ? ticket.daycode.colorCode : '000000') }"
					>
						<span class="receipt__logo-yt">{{ ticket.daycode.textCode }}</span>
						<img src="@/apps/fynbus/assets/images/letbane-logo-text.svg" class="receipt__logo-text" />
					</div>
				</div>
				<div class="receipt__content">
					<span v-if="!onLine" class="receipt__offline">{{ $t('letbane.offline_uppercase') }}</span>
					<template v-else>
						<span v-if="hasExpired" class="receipt__expired">{{ $t('letbane.expired_uppercase') }}</span>
						<span v-if="!hasExpired && invalidTicket" class="receipt__invalid">
							{{ $t('letbane.invalid_ticket_uppercase') }}
						</span>
						<template v-if="!hasExpired && countdown && !invalidTicket">
							<span class="receipt__timer">{{ countdown }}</span>
							<span class="receipt__availability">{{ $t('letbane.your_ticket_is_valid') }}</span>
						</template>
					</template>

					<template v-if="ticket.orderLines && ticket.orderLines.length">
						<template v-if="!invalidTicket">
							<div class="receipt__orders">
								<div v-for="(order, index) in ticket.orderLines" :key="index" class="receipt__order">
									<span class="receipt__label">
										{{ order.totalPassengerCount }}x
										<span class="receipt__label--capitalize">{{ order.productName[selectedLanguage] }}</span>
									</span>
									<span class="receipt__value receipt__value--price">
										{{ order.priceCents | prettyPrice }}
									</span>
								</div>
							</div>
							<div class="receipt__line"></div>
							<div v-if="ticket.customer && ticket.customer.username" class="receipt__phone">
								<span v-if="ticket.customer.usernameType === 'PhoneNumber'" class="receipt__label receipt__label--noshrink">
									{{ $t('letbane.phone_number') }}
								</span>
								<span v-if="ticket.customer.usernameType === 'Email'" class="receipt__label receipt__label--noshrink">
									{{ $t('letbane.email') }}
								</span>
								<span class="receipt__value">
									{{ ticket.customer.username }}
								</span>
							</div>
							<div v-if="ticket.validFromInstant" class="receipt__validfrom">
								<span class="receipt__label">{{ $t('letbane.valid_from') }}</span>
								<span
									class="receipt__date receipt__value"
									:class="{ 'receipt__date--capitalize': selectedLanguage === 'en' }"
								>
									{{ formatDate(ticket.validFromInstant) }}
								</span>
							</div>
							<div v-if="ticket.validToInstant" class="receipt__validto">
								<span class="receipt__label">{{ $t('letbane.valid_to') }}</span>
								<span
									class="receipt__date receipt__value"
									:class="{ 'receipt__date--capitalize': selectedLanguage === 'en' }"
								>
									{{ formatDate(ticket.validToInstant) }}
								</span>
							</div>
							<div class="receipt__line"></div>
							<div v-if="ticket.validToInstant" class="receipt__paid">
								<span class="receipt__label">{{ $t('letbane.paid_uppercase') }}</span>
								<span class="receipt__value">{{ ticket.priceCents | prettyPrice }}</span>
							</div>

							<div class="receipt__line--dashed">
								<div class="receipt__circles">
									<div class="receipt__circle--left"></div>
									<div class="receipt__circle--right"></div>
								</div>
							</div>

							<span v-if="ticket.controlCode" class="receipt__controlcode"> +{{ ticket.controlCode }}+ </span>
						</template>
						<template v-else>
							<div class="receipt__invalid-text">
								{{ $t('letbane.invalid_description') }}
							</div>
							<div class="receipt__line--dashed">
								<div class="receipt__circles">
									<div class="receipt__circle--left"></div>
									<div class="receipt__circle--right"></div>
								</div>
							</div>

							<span class="receipt__controlcode receipt__controlcode--empty"> </span>
						</template>
					</template>
					<template v-else>
						<div class="receipt__invalid-text">
							<!-- {{ $t('letbane.invalid_description_1') }}
							<a
								href="https://www.odenseletbane.dk/kontakt-os"
								title="Odense Letbane kontakt os"
								class="receipt__invalid-text receipt__invalid-text--link reset-button"
							>
								odenseletbane.dk
							</a>
							{{ $t('letbane.invalid_description_2') }} -->
							{{ $t('letbane.invalid_description') }}
						</div>
						<div class="receipt__line--dashed">
							<div class="receipt__circles">
								<div class="receipt__circle--left"></div>
								<div class="receipt__circle--right"></div>
							</div>
						</div>

						<span class="receipt__controlcode"></span>
					</template>
				</div>
				<div
					class="receipt__ribbon"
					:class="[
						{ 'receipt__ribbon--active': !hasExpired && countdown && onLine },
						{ 'receipt__ribbon--expired': hasExpired && onLine },
						{ 'receipt__ribbon--invalid': !hasExpired && onLine && invalidTicket },
						{ 'receipt__ribbon--offline': !onLine }
					]"
				>
					<span v-if="!onLine" class="receipt__ribbon-text">
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
						{{ $t('letbane.offline_uppercase') }} • {{ $t('letbane.offline_uppercase') }} •
					</span>
					<template v-else>
						<span
							v-if="!hasExpired && countdown"
							class="receipt__ribbon-text receipt__ribbon-text--animate"
							:class="[
								{ 'receipt__ribbon-text--da': selectedLanguage === 'da' },
								{ 'receipt__ribbon-text--en': selectedLanguage === 'en' }
							]"
						>
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
							{{ $t('letbane.odense_letbane_uppercase') }} • {{ $t('letbane.odense_letbane_uppercase') }} •
						</span>
						<span v-if="!hasExpired && invalidTicket" class="receipt__ribbon-text">
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
							{{ $t('letbane.invalid_ticket_uppercase') }} • {{ $t('letbane.invalid_ticket_uppercase') }} •
						</span>
						<span v-if="hasExpired" class="receipt__ribbon-text">
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
							{{ $t('letbane.expired_uppercase') }} • {{ $t('letbane.expired_uppercase') }} •
						</span>
					</template>
				</div>
			</div>
			<!-- <div v-if="invalidTicket" class="receipt__notfound">RECEIPT NOT FOUND</div> -->
		</div>
		<router-link
			:to="{ path: '/' }"
			:title="$t('letbane.buy_more_tickets_uppercase')"
			class="page__button reset-button"
			:class="{ 'page__button--active': (hasExpired && onLine) || invalidTicket }"
		>
			<span class="page__button-text">{{ $t('letbane.buy_more_tickets_uppercase') }}</span>
		</router-link>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { callApi } from '@/api/api-core'
import { i18nInstance } from '@/i18n'
import { makeUtcDate } from '@/utilities/time'
import moment from 'moment-timezone'

export default {
	data() {
		return {
			ticket: null,
			countdown: null,
			hasExpired: false,
			onLine: navigator.onLine,
			invalidTicket: false,
			testMode: process.env.VUE_APP_SERVER !== 'live'
		}
	},
	computed: {
		...mapGetters({
			getSecondaryWebshop: 'system/getSecondaryWebshop',
			getServerDateAsMoment: 'date/getServerDateAsMoment'
		}),
		selectedLanguage() {
			return i18nInstance().locale
		},
		odenseTramIdentifier() {
			return this.getSecondaryWebshop.filter(webshop => webshop.identifier === 'OdenseTram')[0].identifier
		}
	},
	watch: {
		'$i18n.locale'() {
			const orderID = this.$route.query.orderId
			const token = this.$cookies.get(orderID)

			if (token) {
				this.formatCountdown()
			}
		}
	},
	mounted() {
		const orderID = this.$route.query.orderId
		const token = this.$cookies.get(orderID) || null

		this.getTicket(orderID, token)
	},
	methods: {
		getTicket(orderID, token) {
			const requestHeaders = {
				'X-MobileTicket-ClientCompany': this.odenseTramIdentifier,
				'X-MobileTicket-ClientLanguage': this.selectedLanguage
			}

			const requestBody = {
				orderId: orderID,
				verificationToken: token
			}

			callApi('POST', 'v2/tickets/view', requestBody, requestHeaders)
				.then(response => {
					this.ticket = response.responseBody

					if (this.ticket && this.ticket.verificationTokenUpdated && this.ticket.verificationToken) {
						this.$cookies.set(orderID, this.ticket.verificationToken, new Date(this.ticket.validToInstant).toUTCString())
					}

					if (this.ticket && this.ticket.customer && this.ticket.customer.usernameType === 'PhoneNumber') {
						this.ticket.customer.username = this.ticket.customer.username.replace('-', ' ')
					}

					this.formatCountdown()

					const countdownInterval = setInterval(() => {
						this.formatCountdown()
					}, 500)

					window.addEventListener('online', this.updateOnlineStatus)
					window.addEventListener('offline', this.updateOnlineStatus)

					this.$once('hook:destroyed', () => {
						clearInterval(countdownInterval)
						window.removeEventListener('online', this.updateOnlineStatus)
						window.removeEventListener('offline', this.updateOnlineStatus)
					})
				})
				.catch(() => {
					console.log('FAILED GETTING TICKET')
				})
		},
		formatCountdown() {
			const now = makeUtcDate(new Date().toISOString()).valueOf()
			const deadline = makeUtcDate(this.ticket.validToInstant).valueOf()
			const remaining = deadline - now
			const duration = moment.duration(remaining)
			const hours = Math.floor(duration.asHours())
			const minutes = duration.minutes()
			const seconds = duration.seconds()

			if (hours > 0) {
				this.countdown = `${hours}${this.$t('letbane.short_hour_unit')} : ${minutes}min`
			} else if (hours <= 0 && minutes > 0) {
				this.countdown = `${minutes}min`
			} else if (hours <= 0 && minutes <= 0 && seconds > 0) {
				this.countdown = `1min`
			} else {
				this.countdown = null
				this.hasExpired = true
			}

			if (!this.hasExpired && this.ticket && !this.ticket.daycode) {
				console.log('FAILED', this.ticket, this.hasExpired)
				this.countdown = null
				this.invalidTicket = true
			}
		},
		formatDate(date) {
			return makeUtcDate(date).format('DD MMMM YYYY HH:mm')
		},
		updateOnlineStatus(e) {
			if (this.countdown) {
				this.onLine = e.type === 'online'
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.page {
	&__receipt {
		margin-bottom: 1.5rem;
	}

	&__button {
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 1rem;
		width: 100%;
		background-color: transparent;
		border: 0.0625rem solid $letbane-color-stroke;
		padding: 1.0625rem;
		border-radius: 0.375rem;
		cursor: pointer;
		transition: background-color 0.3s ease;
		box-sizing: border-box;
		text-decoration: none;

		&:not(:last-of-type) {
			margin-bottom: 1rem;
		}

		&:hover {
			background-color: $letbane-color-identityred-hover;
		}

		&:focus {
			background-color: $letbane-color-identityred-pressed;
		}

		&--active {
			background-color: $letbane-color-identityred;
			border: none;
		}
	}

	&__button-text {
		font-weight: 400;
		font-size: 1rem;
		line-height: 126%;
		color: $letbane-color-black;
		transition: color 0.3s ease;

		.page__button:hover & {
			color: $letbane-color-white;
		}

		.page__button:focus & {
			color: $letbane-color-white;
		}

		.page__button--active & {
			color: $letbane-color-white;
		}
	}
}

.receipt {
	position: relative;
	margin-top: 2.5rem;

	&__wrapper {
		background-color: $letbane-color-white;
		border-top: 0.0625rem solid $letbane-color-stroke;
		border-right: 0.0625rem solid $letbane-color-stroke;
		border-left: 0.0625rem solid $letbane-color-stroke;
		border-radius: 0.25rem;
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 0 1.125rem;
	}

	&__logo-container {
		position: relative;
		width: 100%;
		height: 4.125rem;
		margin-top: -2.1875rem;
		padding-right: 1.125rem;
		box-sizing: border-box;
	}

	&__logo-bg {
		position: absolute;
		right: 1.0625rem;
		width: 4.125rem;
		height: 4.125rem;
		border-radius: 50%;
		background-color: $letbane-color-background;
		border-top: 0.0625rem solid $letbane-color-background;
		border-right: 0.0625rem solid $letbane-color-background;
		border-left: 0.0625rem solid $letbane-color-stroke;
		border-bottom: 0.0625rem solid $letbane-color-stroke;
		transform: rotate(-45deg);
	}

	&__logo-content {
		display: flex;
		justify-content: center;
		align-items: center;
		position: absolute;
		right: 1.375rem;
		bottom: 0.1875rem;
		width: 3.625rem;
		height: 3.625rem;
		border-radius: 50%;
	}

	&__logo-yt {
		position: absolute;
		color: $letbane-color-beige;
		font-size: 1rem;
		font-weight: 700;
		-webkit-text-size-adjust: none;
		-ms-text-size-adjust: none;
		-moz-text-size-adjust: none;
		text-size-adjust: none;
	}

	&__logo-text {
		position: absolute;
		transform-origin: center center;
		animation: rotate 40s linear infinite;
		width: 100%;
		height: 100%;
		box-sizing: border-box;
		padding: 0.1875rem;
	}

	&__expired {
		font-size: 1.625rem;
		line-height: 1;
		font-weight: 700;
		letter-spacing: -0.05em;
		margin-top: 2.4375rem;
		margin-bottom: 2.5625rem;
		color: $letbane-color-999;
		text-align: center;
	}

	&__invalid {
		font-size: 1.625rem;
		line-height: 1;
		font-weight: 700;
		letter-spacing: -0.05em;
		margin-top: 7.0625rem;
		margin-bottom: 1.125rem;
		color: $letbane-color-999;
		text-align: center;
	}

	&__invalid-text {
		font-weight: 300;
		font-size: 0.75rem;
		max-width: 17.1875rem;
		text-align: center;
		margin-bottom: 5.625rem;
		line-height: 1.25rem;

		&--link {
			color: $letbane-color-identityred;
			margin: 0;
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	&__offline {
		font-size: 1.625rem;
		line-height: 1;
		font-weight: 700;
		letter-spacing: -0.05em;
		margin-bottom: 2.5625rem;
		color: $letbane-color-999;
		text-align: center;
	}

	&__timer {
		font-size: 1.625rem;
		line-height: 1;
		font-weight: 400;
		letter-spacing: -0.05em;
		margin-bottom: 0.375rem;
	}

	&__availability {
		font-size: 0.875rem;
		font-weight: 300;
		padding-bottom: 0.9375rem;
	}

	&__orders {
		width: 100%;
	}

	&__order {
		display: flex;
		justify-content: space-between;
		width: 100%;
		margin-bottom: 0.6875rem;
		column-gap: 1rem;

		&:not(:last-of-type) {
			margin-bottom: 0.625rem;
		}
	}

	&__line {
		width: calc(100% + 2.25rem);
		height: 0.0625rem;
		background-color: $letbane-color-stroke;
	}

	&__line--dashed {
		position: relative;
		width: 100%;
		border-bottom: 0.125rem dashed $letbane-color-stroke;
	}

	&__phone {
		display: flex;
		justify-content: space-between;
		column-gap: 1rem;
		width: 100%;
		margin-top: 0.6875rem;
		margin-bottom: 0.25rem;
	}

	&__label {
		align-self: center;

		&--noshrink {
			flex-shrink: 0;
		}

		&--capitalize {
			text-transform: uppercase;
		}
	}

	&__value {
		word-break: break-all;
		text-align: end;
		align-self: center;

		&--price {
			flex-shrink: 0;
		}
	}

	&__validfrom {
		display: flex;
		justify-content: space-between;
		width: 100%;
		margin-bottom: 0.25rem;
	}

	&__validto {
		display: flex;
		justify-content: space-between;
		width: 100%;
		margin-bottom: 0.6875rem;
	}

	&__date {
		text-transform: lowercase;

		&--capitalize {
			text-transform: capitalize;
		}
	}

	&__paid {
		position: relative;
		display: flex;
		justify-content: space-between;
		width: 100%;
		margin-top: 0.6875rem;
		margin-bottom: 1.625rem;
	}

	&__circles {
		display: flex;
		justify-content: space-between;
		position: absolute;
		height: 1.75rem;
		top: -0.875rem;
		left: -2.125rem;
		right: -2.125rem;
	}

	&__circle--left {
		width: 1.75rem;
		height: 1.75rem;
		background-color: $letbane-color-background;
		border-radius: 50%;
		border-top: 0.0625rem solid $letbane-color-stroke;
		border-right: 0.0625rem solid $letbane-color-stroke;
		border-left: 0.0625rem solid $letbane-color-background;
		border-bottom: 0.0625rem solid $letbane-color-background;
		transform: rotate(45deg);
	}

	&__circle--right {
		width: 1.75rem;
		height: 1.75rem;
		background-color: $letbane-color-background;
		border-radius: 50%;
		border-top: 0.0625rem solid $letbane-color-background;
		border-right: 0.0625rem solid $letbane-color-background;
		border-left: 0.0625rem solid $letbane-color-stroke;
		border-bottom: 0.0625rem solid $letbane-color-stroke;
		transform: rotate(45deg);
	}

	&__controlcode {
		font-size: 1.625rem;
		line-height: 1;
		font-weight: 400;
		margin-top: 1rem;
		margin-bottom: 1rem;

		&--empty {
			height: 2rem;
		}
	}

	&__ribbon {
		position: relative;
		display: flex;
		align-items: center;
		overflow: hidden;
		width: calc(100% + 0.125rem);
		height: 1.0625rem;
		margin-left: -0.0625rem;
		border-bottom-right-radius: 0.25rem;
		border-bottom-left-radius: 0.25rem;

		&--active {
			background-color: $letbane-color-identityred;
			color: $letbane-color-beige;
		}

		&--expired,
		&--offline,
		&--invalid {
			justify-content: center;
			background-color: $letbane-color-999;
			color: $letbane-color-white;
		}
	}

	&__ribbon-text {
		position: absolute;
		white-space: nowrap;
		font-weight: 300;
		font-size: 0.625rem;
		line-height: 126%;
		text-transform: uppercase;

		&--animate.receipt__ribbon-text--da {
			animation: marquee-da 2s linear infinite;
		}

		&--animate.receipt__ribbon-text--en {
			animation: marquee-en 2s linear infinite;
		}
	}
}

@keyframes rotate {
	to {
		transform: rotate(360deg);
	}
}

@keyframes marquee-da {
	0% {
		left: 0;
	}
	100% {
		left: -99px;
	}
}

@keyframes marquee-en {
	0% {
		left: 0;
	}
	100% {
		left: -103px;
	}
}

.testMode {
	position: absolute;
	top: 30%;
	left: 8%;
	color: red;
	font-size: 3rem;
	font-weight: 400;
	letter-spacing: 3px;
	transform: rotate(50deg);
	white-space: nowrap;
	z-index: 100;
}
</style>
